import { useMemo, useState } from "react";
import {
  useProcessCasesByEngagement,
  useStepsByProcessCase,
  useActivitiesByProcessCase,
  useCompleteActivity,
  type OpsV2Error,
  type StepInstanceOut,
  type ActivityInstanceOut,
} from "../../../api/opsV2";
import { hasPermissionInCurrentOrg } from "../../../state/orgContext";
import { AppShell } from "../../layouts/AppShell";
import { ForbiddenPanel } from "../../components/ops/ForbiddenPanel";
import { NotAccessiblePanel } from "../../components/ops/NotAccessiblePanel";
import { ActivityCompleteDrawer } from "../../components/ops/ActivityCompleteDrawer";
import { ArrowLeft, CheckCircle } from "lucide-react";

const STEP_STATUS_LABELS: Record<string, string> = {
  pending: "En attente",
  active: "Actif",
  blocked: "Bloqué",
  completed: "Terminé",
  cancelled: "Annulé",
  skipped: "Ignoré",
};

const ACTIVITY_STATUS_LABELS: Record<string, string> = {
  pending: "En attente",
  active: "Actif",
  blocked: "Bloqué",
  done: "Terminé",
  cancelled: "Annulé",
  skipped: "Ignoré",
};

type ProcessCaseDetailPageProps = {
  engagementId: string;
  processCaseId: string;
  userName?: string;
  onBackToEngagement?: () => void;
};

export function ProcessCaseDetailPage({
  engagementId,
  processCaseId,
  userName = "Utilisateur",
  onBackToEngagement,
}: ProcessCaseDetailPageProps) {
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [drawerActivity, setDrawerActivity] = useState<ActivityInstanceOut | null>(null);
  const [submitError, setSubmitError] = useState<OpsV2Error | null>(null);

  const { data: processCases, loading: loadingCases, error: errorCases } = useProcessCasesByEngagement(engagementId);
  const { data: steps, loading: loadingSteps } = useStepsByProcessCase(processCaseId);
  const { data: activities, loading: loadingActivities, refetch: refetchActivities } = useActivitiesByProcessCase(processCaseId);
  const completeActivity = useCompleteActivity();
  const canComplete = hasPermissionInCurrentOrg("ops.activity.complete");
  const canAddCost = hasPermissionInCurrentOrg("finance.cost.write");

  const processCase = useMemo(
    () => processCases?.find((pc) => pc.id === processCaseId) ?? null,
    [processCases, processCaseId]
  );

  const apiError = errorCases as OpsV2Error | undefined;
  const is403 = apiError?.status === 403;
  const is404 = apiError?.status === 404 || (!loadingCases && !processCase && processCases);

  const modules = useMemo(
    () => [
      { label: "Tableau de bord", onClick: () => (window.location.hash = "#/") },
      { label: "Dossiers", onClick: () => (window.location.hash = "#/ops/engagements") },
      { label: "Dossier", onClick: onBackToEngagement },
    ],
    [onBackToEngagement]
  );

  const handleOpenDrawer = (activity: ActivityInstanceOut) => {
    setSubmitError(null);
    setDrawerActivity(activity);
  };

  const handleDrawerSubmit = async (activityId: string, runInput: Record<string, unknown>) => {
    if (!canComplete) return;
    setSubmitError(null);
    setCompletingId(activityId);
    try {
      await completeActivity(activityId, { run_input_json: runInput });
      refetchActivities();
      setDrawerActivity(null);
    } catch (err) {
      setSubmitError(err as OpsV2Error);
    } finally {
      setCompletingId(null);
    }
  };

  if (is403) {
    return (
      <AppShell topbar={<div className="text-sm text-muted">Dossier de processus</div>} userName={userName} modules={modules}>
        <ForbiddenPanel message={errorCases?.message} onBack={onBackToEngagement} />
      </AppShell>
    );
  }

  if (is404 || (errorCases && !loadingCases)) {
    return (
      <AppShell topbar={<div className="text-sm text-muted">Dossier de processus</div>} userName={userName} modules={modules}>
        <NotAccessiblePanel message={errorCases?.message} onBack={onBackToEngagement} />
      </AppShell>
    );
  }

  if (loadingCases && !processCase) {
    return (
      <AppShell topbar={<div className="text-sm text-muted">Dossier de processus</div>} userName={userName} modules={modules}>
        <div className="p-8 text-center text-sm text-muted-foreground">Chargement…</div>
      </AppShell>
    );
  }

  if (!processCase) return null;

  const activitiesByStep = useMemo(() => {
    if (!activities?.length) return new Map<string, ActivityInstanceOut[]>();
    const map = new Map<string, ActivityInstanceOut[]>();
    for (const a of activities) {
      const list = map.get(a.step_instance_id) ?? [];
      list.push(a);
      map.set(a.step_instance_id, list);
    }
    return map;
  }, [activities]);

  return (
    <AppShell
      topbar={<div className="text-sm text-muted">Dossier de processus</div>}
      userName={userName}
      modules={modules}
    >
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          {onBackToEngagement && (
            <button
              type="button"
              onClick={onBackToEngagement}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-text hover:bg-surface-muted"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour au dossier
            </button>
          )}
          <h1 className="text-section text-text">Dossier de processus</h1>
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">{processCase.status}</span>
        </div>

        <div className="rounded-lg border border-border bg-surface p-4">
          <h2 className="text-sm font-semibold text-text">Plan d’exécution</h2>
          {loadingSteps ? (
            <p className="mt-2 text-sm text-muted-foreground">Chargement des étapes…</p>
          ) : !steps?.length ? (
            <p className="mt-2 text-sm text-muted-foreground">Aucune étape.</p>
          ) : (
            <ul className="mt-3 space-y-4">
              {steps.map((step) => (
                <StepBlock
                  key={step.id}
                  step={step}
                  activities={activitiesByStep.get(step.id) ?? []}
                  loadingActivities={loadingActivities}
                  canComplete={canComplete}
                  canAddCost={canAddCost}
                  completingId={completingId}
                  onOpenDrawer={handleOpenDrawer}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      {drawerActivity && (
        <ActivityCompleteDrawer
          activity={drawerActivity}
          onClose={() => { setDrawerActivity(null); setSubmitError(null); }}
          onSubmit={handleDrawerSubmit}
          submitError={submitError}
          onDismissError={() => setSubmitError(null)}
          submitting={completingId === drawerActivity.id}
        />
      )}
    </AppShell>
  );
}

function StepBlock({
  step,
  activities,
  loadingActivities,
  canComplete,
  canAddCost,
  completingId,
  onOpenDrawer,
}: {
  step: StepInstanceOut;
  activities: ActivityInstanceOut[];
  loadingActivities: boolean;
  canComplete: boolean;
  canAddCost: boolean;
  completingId: string | null;
  onOpenDrawer: (activity: ActivityInstanceOut) => void;
}) {
  const showComplete = (a: ActivityInstanceOut) => {
    if (!canComplete || (a.status !== "pending" && a.status !== "active")) return false;
    if (a.activity_type === "add_cost" && !canAddCost) return false;
    return true;
  };

  return (
    <li className="rounded-md border border-border bg-surface-muted/30 p-3">
      <div className="flex items-center justify-between">
        <span className="font-medium text-text">{step.title}</span>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
          {STEP_STATUS_LABELS[step.status] ?? step.status}
        </span>
      </div>
      {loadingActivities ? (
        <p className="mt-2 text-xs text-muted-foreground">Chargement des activités…</p>
      ) : activities.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">Aucune activité.</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {activities.map((a) => (
            <li
              key={a.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded border border-border bg-surface px-3 py-2 text-sm"
            >
              <span className="text-text">{a.title}</span>
              <span className="rounded bg-muted px-1.5 py-0.5 text-xs">
                {ACTIVITY_STATUS_LABELS[a.status] ?? a.status}
              </span>
              {a.activity_type && (
                <span className="text-xs text-muted-foreground">Type: {a.activity_type}</span>
              )}
              {showComplete(a) && (
                <button
                  type="button"
                  disabled={completingId === a.id}
                  onClick={() => onOpenDrawer(a)}
                  className="inline-flex items-center gap-1 rounded-md bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50"
                >
                  <CheckCircle className="h-3 w-3" />
                  Compléter
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
